import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Trash2, AlertTriangle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

/**
 * WipeEmaDataCard — lets a whitelisted user permanently delete all their
 * Experience Tracking data (entries, prompts, profile interpretations).
 * Visible only to users in VOICE_MOOD_ALLOWED_EMAILS (server enforces 403 for others).
 */
export const WipeEmaDataCard = () => {
  const [visible, setVisible] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [wiping, setWiping] = useState(false);
  const REQUIRED_PHRASE = "CANCELLA";

  useEffect(() => {
    // Probe whitelist by hitting an EMA endpoint that is whitelist-gated.
    api.get("/voice-mood/access")
      .then((r) => setVisible(!!r.data?.has_access))
      .catch(() => setVisible(false));
  }, []);

  if (!visible) return null;

  const handleWipe = async () => {
    if (confirmText.trim().toUpperCase() !== REQUIRED_PHRASE) {
      toast.error(`Digita "${REQUIRED_PHRASE}" per confermare`);
      return;
    }
    setWiping(true);
    try {
      const res = await api.post("/voice-mood/admin/wipe-my-data");
      const total =
        (res.data?.entries_deleted || 0) +
        (res.data?.daily_prompts_deleted || 0) +
        (res.data?.interpretations_deleted || 0);
      toast.success(`Dati Check-in cancellati (${total} record)`);
      setConfirmOpen(false);
      setConfirmText("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Cancellazione fallita");
    } finally {
      setWiping(false);
    }
  };

  return (
    <>
      <Card className="border-2 border-rose-200 bg-rose-50/40 shadow-sm rounded-sm" data-testid="wipe-ema-card">
        <CardHeader className="pb-2">
          <CardTitle className="font-heading text-lg flex items-center gap-2 text-rose-900">
            <Trash2 className="w-5 h-5" strokeWidth={1.5} />
            Cancella i miei dati Check-in
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-rose-900/80 leading-relaxed">
            Elimina <strong>definitivamente</strong> tutti i tuoi check-in EMA,
            i prompt programmati e le interpretazioni dei profili.
            Operazione <strong>irreversibile</strong>. Il tuo account resta attivo.
          </p>
          <Button
            variant="destructive"
            onClick={() => setConfirmOpen(true)}
            className="rounded-sm"
            data-testid="wipe-ema-open-btn"
          >
            <Trash2 className="w-4 h-4 mr-1.5" />
            Cancella tutti i miei dati EMA
          </Button>
        </CardContent>
      </Card>

      <Dialog open={confirmOpen} onOpenChange={(o) => { if (!wiping) setConfirmOpen(o); }}>
        <DialogContent className="max-w-md" data-testid="wipe-ema-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-rose-600" />
              Sei sicura?
            </DialogTitle>
            <DialogDescription className="pt-2">
              Verranno cancellati <strong>definitivamente</strong> tutti i tuoi check-in,
              i prompt e le interpretazioni dei profili. Non c'è modo di recuperarli.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-2 py-2">
            <label className="text-xs text-muted-foreground">
              Per confermare, digita <code className="bg-muted px-1.5 py-0.5 rounded text-foreground">{REQUIRED_PHRASE}</code> qui sotto:
            </label>
            <input
              type="text"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              autoFocus
              className="w-full px-3 py-2 text-sm border-2 border-rose-200 rounded-sm focus:outline-none focus:border-rose-500"
              placeholder={REQUIRED_PHRASE}
              data-testid="wipe-ema-confirm-input"
            />
          </div>

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => { setConfirmOpen(false); setConfirmText(""); }}
              disabled={wiping}
              data-testid="wipe-ema-cancel-btn"
            >
              Annulla
            </Button>
            <Button
              variant="destructive"
              onClick={handleWipe}
              disabled={wiping || confirmText.trim().toUpperCase() !== REQUIRED_PHRASE}
              data-testid="wipe-ema-confirm-btn"
            >
              {wiping ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Trash2 className="w-4 h-4 mr-1.5" />}
              {wiping ? "Cancello..." : "Cancella definitivamente"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default WipeEmaDataCard;
